import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Descriptions, Image, Tag, Input } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, AuditOutlined, ReloadOutlined, FileTextOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { renderDateTime } from './utils';

const { TextArea } = Input;

interface User {
  id: number;
  username: string;
  phone: string;
  create_time: string;
  company_name: string;
  account: string;
  email: string;
  type: string;
  authorization_letter: string;
  establish_date: string;
  register_address: string;
  authorized_representative_name: string;
  authorized_representative_id_card: string;
  authorized_representative_id_card_front: string;
  authorized_representative_id_card_back: string;
  legal_person_name: string;
  legal_person_id_card: string;
  legal_person_id_card_front: string;
  legal_person_id_card_back: string;
  business_license: string;
}

// 格式化身份证号：在第6、14位后分别加空格
const formatIdCard = (idCard: string | null | undefined): string => {
  if (!idCard) return '-';
  if (idCard.length !== 18) return idCard;
  return `${idCard.slice(0, 6)} ${idCard.slice(6, 14)} ${idCard.slice(14)}`;
};

export default function AccountSupplierAudit() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingUser, setRejectingUser] = useState<User | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/users/pending', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setUsers(resp.data || []);
      } else {
        message.error(resp?.error || '获取待审核供应商列表失败');
      }
    } catch (err) {
      message.error('获取待审核供应商列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = (url: string) => {
    setPdfUrl(url);
    setPdfModalVisible(true);
  };

  const handleViewDetail = (record: User) => {
    setSelectedUser(record);
    setDetailModalVisible(true);
  };

  const handleApprove = async (id: number, phone: string, closeModal: boolean = false) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/users/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ phone }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('审核通过');
        if (closeModal) {
          setDetailModalVisible(false);
          setSelectedUser(null);
        }
        fetchUsers();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 打开拒绝原因弹窗
  const openRejectModal = (user: User) => {
    setRejectingUser(user);
    setRejectReason('');
    setRejectModalVisible(true);
  };

  // 确认拒绝
  const handleRejectConfirm = async () => {
    if (!rejectingUser) return;
    if (!rejectReason.trim()) {
      message.error('请填写拒绝原因');
      return;
    }
    if (rejectReason.length > 50) {
      message.error('拒绝原因不能超过50个字');
      return;
    }
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/users/${rejectingUser.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: rejectingUser.phone, reason: rejectReason }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('已拒绝');
        setRejectModalVisible(false);
        setRejectingUser(null);
        setRejectReason('');
        setDetailModalVisible(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: '联系人姓名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '联系人电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
    },
    {
      title: '申请时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 160,
      render: renderDateTime,
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: '纳税人识别号',
      dataIndex: 'account',
      key: 'account',
      width: 180,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#6b7280' }}>{text}</span>
    },
    {
      title: '法人授权书',
      dataIndex: 'authorization_letter',
      key: 'authorization_letter',
      width: 120,
      render: (url) => url ? (
        <Button type="link" onClick={() => handleViewPdf(url)} icon={<FileTextOutlined />}>
          查看授权书
        </Button>
      ) : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看详情
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record.id, record.phone)}
            style={{ background: '#10b981', borderColor: '#10b981' }}
            size="small"
          >
            通过
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            onClick={() => openRejectModal(record)}
            size="small"
          >
            拒绝
          </Button>
        </Space>
      ),
    },
  ];

  const renderFieldValue = (value: any, fieldName: string) => {
    if (!value) return <span style={{ color: '#999' }}>-</span>;
    
    // 如果是URL类型的字段
    const urlFields = [
      'authorization_letter', 'business_license', 
      'authorized_representative_id_card_front', 'authorized_representative_id_card_back',
      'legal_person_id_card_front', 'legal_person_id_card_back'
    ];
    
    if (urlFields.includes(fieldName)) {
      // 判断是图片还是PDF
      if (value.toLowerCase().endsWith('.pdf')) {
        return (
          <Button type="link" onClick={() => handleViewPdf(value)}>
            查看文件
          </Button>
        );
      } else {
        return <Image src={value} width={100} />;
      }
    }
    
    return value;
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <AuditOutlined />
          </div>
          <div>
            <h2>供应商审核</h2>
            <p>审核待审供应商申请</p>
          </div>
        </div>
        
        <Button 
          icon={<ReloadOutlined />} 
          onClick={fetchUsers}
          style={{ borderRadius: 10 }}
        >
          刷新
        </Button>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{users.length}</span> 条待审核
        </div>
        
        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 详情弹窗 */}
      <Modal
        title="供应商详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          selectedUser ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setDetailModalVisible(false)}>关闭</Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={submitting}
                onClick={() => handleApprove(selectedUser.id, selectedUser.phone, true)}
                style={{ background: '#10b981', borderColor: '#10b981' }}
              >
                通过
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => openRejectModal(selectedUser)}
              >
                拒绝
              </Button>
            </div>
          ) : null
        }
        width="calc(800px + 15vw)"
      >
        {selectedUser && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="公司名称" span={2}>{selectedUser.company_name}</Descriptions.Item>
            <Descriptions.Item label="纳税人识别号">
              <span style={{ 
                display: 'inline-block', 
                whiteSpace: 'nowrap', 
                overflowX: 'auto',
                maxWidth: '100%',
                fontFamily: 'monospace'
              }}>
                {selectedUser.account}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="联系人">{selectedUser.username}</Descriptions.Item>
            <Descriptions.Item label="联系电话">{selectedUser.phone}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{selectedUser.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="成立日期">{selectedUser.establish_date ? selectedUser.establish_date.split('T')[0] : '-'}</Descriptions.Item>
            <Descriptions.Item label="注册地址" span={2}>{selectedUser.register_address || '-'}</Descriptions.Item>
            <Descriptions.Item label="授权代表姓名">{selectedUser.authorized_representative_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="授权代表身份证号">
              <span style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>
                {formatIdCard(selectedUser.authorized_representative_id_card)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="授权代表身份证正面">
              {renderFieldValue(selectedUser.authorized_representative_id_card_front, 'authorized_representative_id_card_front')}
            </Descriptions.Item>
            <Descriptions.Item label="授权代表身份证背面">
              {renderFieldValue(selectedUser.authorized_representative_id_card_back, 'authorized_representative_id_card_back')}
            </Descriptions.Item>
            <Descriptions.Item label="法人姓名">{selectedUser.legal_person_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="法人身份证号">
              <span style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>
                {formatIdCard(selectedUser.legal_person_id_card)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="法人身份证正面">
              {renderFieldValue(selectedUser.legal_person_id_card_front, 'legal_person_id_card_front')}
            </Descriptions.Item>
            <Descriptions.Item label="法人身份证背面">
              {renderFieldValue(selectedUser.legal_person_id_card_back, 'legal_person_id_card_back')}
            </Descriptions.Item>
            <Descriptions.Item label="营业执照" span={2}>
              {renderFieldValue(selectedUser.business_license, 'business_license')}
            </Descriptions.Item>
            <Descriptions.Item label="授权委托书" span={2}>
              {renderFieldValue(selectedUser.authorization_letter, 'authorization_letter')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      
      {/* 拒绝原因弹窗 */}
      <Modal
        title="拒绝原因"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectingUser(null);
          setRejectReason('');
        }}
        onOk={handleRejectConfirm}
        okText="确定"
        cancelText="取消"
        confirmLoading={submitting}
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 8, color: '#666' }}>
          请填写拒绝原因（不超过50个字）：
        </div>
        <TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="请输入拒绝原因"
          maxLength={50}
          showCount
          rows={4}
        />
      </Modal>
      
      {/* PDF预览弹窗 */}
      <Modal
        title="文件预览"
        open={pdfModalVisible}
        onCancel={() => setPdfModalVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '70vh', border: 'none' }}
            title="PDF预览"
          />
        )}
      </Modal>
    </div>
  );
}

